import { useFormContext, useWatch } from "react-hook-form";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import Divider from "@/components/Divider";
import type { FormValues } from "../validation/schema";
import { buildPaymentSummaryLineItems, getPaymentSummaryTotals } from "@/pages/events/eventFeeUtils";
import { isSeminarFormEvent } from "../utils";

const PaymentSummary = () => {
  const { control } = useFormContext<FormValues>();
  const eventTypeName = useWatch({
    control,
    name: "eventType.name"
  });
  const orderTemplateId = useWatch({
    control,
    name: "orderTemplateId"
  });
  const eventFees = useWatch({
    control,
    name: "eventFees"
  });
  const products = useWatch({
    control,
    name: "products"
  });

  const isSeminarPricing = isSeminarFormEvent({
    eventType: { name: eventTypeName },
    orderTemplateId,
    eventFees
  });
  const lineItems = isSeminarPricing
    ? buildPaymentSummaryLineItems(
        (eventFees ?? []).flatMap((eventFee) => {
          if (eventFee.kind === "external") {
            if (!eventFee.displayName.trim() || eventFee.amountCents <= 0) {
              return [];
            }

            return [
              {
                name: eventFee.displayName.trim(),
                amountCents: eventFee.amountCents,
                paymentCollection: "external_system"
              }
            ];
          }

          if (!eventFee.productVariantName || eventFee.amountCents <= 0) {
            return [];
          }

          return [
            {
              name: eventFee.productVariantName,
              amountCents: eventFee.amountCents,
              paymentCollection: eventFee.paymentCollection
            }
          ];
        })
      )
    : buildPaymentSummaryLineItems(
        (products ?? []).flatMap((product) => {
          if (!product.variantName || product.variantPrice <= 0) {
            return [];
          }

          return [
            {
              name: product.variantName,
              amountCents: product.variantPrice,
              paymentCollection: product.collection === "onsite" ? "on_site" : "registration"
            }
          ];
        })
      );

  if (lineItems.length === 0) {
    return null;
  }

  const { dueNowTotal, dueOnSiteTotal, externalSystemTotal, totalFees } = getPaymentSummaryTotals(lineItems);
  const showBreakdown = dueOnSiteTotal > 0 || externalSystemTotal > 0;

  return (
    <div>
      <h3 className="text-title-medium mb-3">Payment Summary (Registrant view)</h3>
      <>
        {lineItems.map((item, index: number) => (
          <div key={index} className="flex justify-between items-center text-body-normal py-2">
            <div className="flex flex-col">
              <span>{item.name}</span>
              {item.subText && (
                <span className="text-body-medium text-sys-brand-on-surface-variant">{item.subText}</span>
              )}
            </div>
            <span>{centsToDollarsFormatted(item.price)}</span>
          </div>
        ))}
        {lineItems.length > 0 && (
          <div className="py-3">
            <Divider />
          </div>
        )}
        <div className="flex justify-between text-title-medium py-2">
          <span>Total Fees</span>
          <span>{centsToDollarsFormatted(totalFees)}</span>
        </div>
        {showBreakdown && (
          <>
            {dueOnSiteTotal > 0 && (
              <div className="flex justify-between text-body-normal py-2">
                <span>Due Onsite</span>
                <span>{centsToDollarsFormatted(dueOnSiteTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-body-normal py-2">
              <span>Due Now</span>
              <span>{centsToDollarsFormatted(dueNowTotal)}</span>
            </div>
            {externalSystemTotal > 0 && (
              <div className="flex justify-between text-body-normal py-2">
                <span>External System</span>
                <span>{centsToDollarsFormatted(externalSystemTotal)}</span>
              </div>
            )}
          </>
        )}
      </>
    </div>
  );
};

export default PaymentSummary;
